import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { motionOn, finePointer } from '@/lib/motion';

// Depth slots, front to back. Each card sits in one slot; every few seconds the
// front card drops away and re-enters at the back.
const SLOTS = [
  { x: 0, y: 0, z: 0, s: 1, o: 1, b: 1 },
  { x: 54, y: -46, z: -140, s: 0.94, o: 0.9, b: 0.62 },
  { x: 108, y: -92, z: -280, s: 0.88, o: 0.75, b: 0.4 },
];

function Card({ shot }) {
  return (
    <div className="absolute inset-0 rounded-[14px] overflow-hidden bg-[#0E0E15] ring-1 ring-white/[0.12] shadow-[0_40px_80px_-30px_rgba(5,5,12,.95)]">
      <div className="flex items-center gap-2 h-8 px-3.5 border-b border-white/[0.08] bg-[#13131B]">
        <span className="h-2 w-2 rounded-full bg-white/15" />
        <span className="h-2 w-2 rounded-full bg-white/15" />
        <span className="h-2 w-2 rounded-full bg-white/15" />
        <span className="ml-3 flex-1 max-w-[220px] h-[18px] rounded-md bg-white/[0.05] font-mono text-[10px] leading-[18px] px-2.5 text-ink-muted truncate">{shot.domain}</span>
      </div>
      <img src={shot.src} alt={shot.alt} loading="eager" decoding="async" className="block w-full h-[calc(100%-2rem)] object-cover object-top" />
    </div>
  );
}

/**
 * Hero visual: a tilted 3D stack of real product screenshots. Cards cycle front
 * to back and the whole stack leans toward the pointer. Static under reduced
 * motion and in the prerender.
 */
export function HeroScreens({ shots, className = '' }) {
  const stage = useRef(null);
  const tilt = useRef(null);
  const cards = useRef([]);
  const label = useRef(null);

  useEffect(() => {
    const els = cards.current.filter(Boolean);
    const place = (el, slot, immediate) => {
      const s = SLOTS[slot];
      const vars = { x: s.x, y: s.y, z: s.z, scale: s.s, opacity: s.o, filter: `brightness(${s.b})`, zIndex: 10 - slot };
      if (immediate) gsap.set(el, vars);
      else gsap.to(el, { ...vars, duration: 1.2, ease: 'expo.inOut' });
    };
    let order = els.map((_, i) => i); // order[slot] = card index
    order.forEach((ci, slot) => place(els[ci], slot, true));
    if (!motionOn()) return undefined;

    const ctx = gsap.context(() => {
      gsap.from(els, { y: 80, opacity: 0, duration: 1.4, ease: 'expo.out', stagger: 0.12, delay: 0.25 });
    });

    const setLabel = () => {
      if (label.current) label.current.textContent = shots[order[0]].name;
    };
    const cycle = () => {
      const front = els[order[0]];
      order = [...order.slice(1), order[0]];
      gsap.timeline()
        .to(front, { y: 120, z: 120, opacity: 0, duration: 0.6, ease: 'power3.in' })
        .add(() => {
          place(front, SLOTS.length - 1, true);
          gsap.set(front, { opacity: 0, y: SLOTS[SLOTS.length - 1].y - 40 });
        })
        .to(front, { opacity: SLOTS[SLOTS.length - 1].o, y: SLOTS[SLOTS.length - 1].y, duration: 0.9, ease: 'expo.out' });
      order.slice(0, -1).forEach((ci, slot) => place(els[ci], slot, false));
      setTimeout(setLabel, 500);
    };
    const id = setInterval(() => { if (!document.hidden) cycle(); }, 4200);

    let cleanupPointer = () => {};
    if (finePointer()) {
      const ry = gsap.quickTo(tilt.current, 'rotationY', { duration: 1.2, ease: 'power3' });
      const rx = gsap.quickTo(tilt.current, 'rotationX', { duration: 1.2, ease: 'power3' });
      const onMove = (e) => {
        const nx = e.clientX / window.innerWidth - 0.5;
        const ny = e.clientY / window.innerHeight - 0.5;
        ry(-16 + nx * 14);
        rx(8 - ny * 10);
      };
      window.addEventListener('pointermove', onMove, { passive: true });
      cleanupPointer = () => window.removeEventListener('pointermove', onMove);
    }

    return () => {
      clearInterval(id);
      cleanupPointer();
      ctx.revert();
      gsap.killTweensOf(els);
    };
  }, [shots]);

  return (
    <div ref={stage} className={`relative [perspective:1800px] ${className}`} aria-hidden>
      <div
        ref={tilt}
        className="relative h-full w-full [transform-style:preserve-3d]"
        style={{ transform: 'rotateY(-16deg) rotateX(8deg)' }}
      >
        {shots.map((s, i) => (
          <div
            key={s.src}
            ref={(el) => { cards.current[i] = el; }}
            className="absolute left-0 top-0 w-[86%] aspect-[16/10.6] will-change-transform"
            style={{ transform: `translate3d(${SLOTS[i].x}px, ${SLOTS[i].y}px, ${SLOTS[i].z}px) scale(${SLOTS[i].s})`, opacity: SLOTS[i].o, zIndex: 10 - i }}
          >
            <Card shot={s} />
          </div>
        ))}
      </div>
      <div className="nd-mono absolute left-0 -bottom-10 flex items-center gap-2.5 !text-[11px]">
        <span className="nd-live" />
        <span>Live now ·</span>
        <span ref={label} className="text-white">{shots[0].name}</span>
      </div>
    </div>
  );
}
